// js/components/SearchBar.js
import Delegate from 'ftdomdelegate';

export class SearchBar extends HTMLElement {
  connectedCallback() {
    this.delegateElement = new Delegate(this);
    this.headerElement = this.closest(".header");
    this.searchBarElement = this.querySelector(".search-bar");
    this.inputElement = this.querySelector('[name="q"]');
    this.searchMenuElement = this.querySelector(".search-bar__menu-wrapper");
    this.searchResultsElement = this.querySelector(".search-bar__results");
    // this.closeButtonElement = this.querySelector(".search-bar__close-button");
    this.productTypeFilter = "";
    this.isSearchOpen = false;
    
    // Referințe pentru butonul de meniu mobil
    this.mobileMenuToggleElement = document.querySelector('[aria-controls="mobile-menu"]');
    this.mobileMenuElement = document.querySelector("#mobile-menu");
    
    // Load translations from data-settings
    this.translations = this._loadTranslations();
    
    this._attachListeners();
  }

  disconnectedCallback() {
    this.delegateElement.off();
    if (this.mobileMenuToggleElement && this._mobileMenuClickHandler) {
      this.mobileMenuToggleElement.removeEventListener("click", this._mobileMenuClickHandler);
    }
  }

  _attachListeners() {
    this.delegateElement.on("click", '[data-action="clear-input"]', this._clearInput.bind(this));
    this.delegateElement.on("click", '[data-action="unfix-search"]', this._unfixMobileSearch.bind(this));
    this.delegateElement.on("focusin", '[name="q"]', this._onInputFocus.bind(this));
    document.addEventListener("click", this._onFocusOut.bind(this));
    this.delegateElement.on("keydown", '[name="q"]', this._handleTab.bind(this));
    this.delegateElement.on("input", '[name="q"]', this._debounce(this._doSearch.bind(this), 250));
    this.delegateElement.on("change", "#search-product-type", this._productTypeChanged.bind(this));
    this.delegateElement.on("submit", this._onFormSubmit.bind(this));
    
    // Listener pentru butonul de meniu mobil - interceptează click-ul când search-ul este deschis
    if (this.mobileMenuToggleElement) {
      this._mobileMenuClickHandler = this._onMobileMenuToggle.bind(this);
      this.mobileMenuToggleElement.addEventListener("click", this._mobileMenuClickHandler);
    }
  }

  /**
   * Toggle the mobile search (only applicable if the show condensed search is shown)
   */
  toggleMobileSearch() {
    this.classList.toggle("is-open");
    this.isSearchOpen = !this.isSearchOpen;
  }

  _unfixMobileSearch() {
    this.classList.remove("is-open");
    // this.closeButtonElement.style.width = "";
    this.searchResultsElement.setAttribute("aria-hidden", "true");
    this.classList.remove("is-filled");
    document.body.classList.remove("no-mobile-scroll");
    this.searchMenuElement?.setAttribute("aria-hidden", "true");
    this.isSearchOpen = false;
  }

  /**
   * Only on mobile
   */
  _clearInput() {
    this.inputElement.value = "";
    this.classList.remove("is-filled");

    this._showEmptyState();
  }

  /**
   * When the input get focus, we display the quick links if they were specified by the merchant, or we open results if
   * results were previously loaded
   */
  _onInputFocus(event) {
    event.stopPropagation();
    // Deschide search-ul dacă nu este deschis
    if (!this.isSearchOpen) {
      this.classList.add("is-open");
      this.isSearchOpen = true;
    }
    
    // Afișează întotdeauna rezultatele la focus, indiferent dacă input-ul are text sau nu
    this.searchResultsElement.setAttribute("aria-hidden", "false");
    this.searchMenuElement?.setAttribute("aria-hidden", "true");
    
    // Adaugă is-filled doar dacă input-ul nu este gol
    if (this.inputElement.value.trim() !== "") {
      this.classList.add("is-filled");
    }

    // Actualizează label-ul butonului de meniu mobil
    this._updateMobileMenuButtonLabel();
    
    // Blochează scroll-ul pe body pe mobile
    if (window.innerWidth <= 999) {
      document.body.classList.add("no-mobile-scroll");
    }
  }

  /**
   * Whenever the focus leaves the search, we close everything (both search results and quick links)
   */
  _onFocusOut(event) {
    // Nu închide dacă click-ul este în interiorul search bar-ului
    if (this.contains(event.target)) {
      return;
    }

    setTimeout(() => {
      document.body.classList.remove("no-mobile-scroll");
      if (!this.contains(event.relatedTarget)) {
        this.classList.remove("is-open");
        this.searchResultsElement.setAttribute("aria-hidden", "true");
        this.searchMenuElement?.setAttribute("aria-hidden", "true");
        this.isSearchOpen = false;
        
        // Actualizează label-ul butonului de meniu mobil
        this._updateMobileMenuButtonLabel();
      }
    }, 50);
  }

  _handleTab(event) {
    if (event.key !== "Tab") {
      return;
    }
    
    // If there are search results, focus on the first one
    const firstFocusableElement = this.searchResultsElement.querySelector("a");
    if (firstFocusableElement) {
      firstFocusableElement.focus();
      event.preventDefault();
      return;
    }
    
    // Otherwise close the search
    this.searchResultsElement.setAttribute("aria-hidden", "true");
    this.searchMenuElement?.setAttribute("aria-hidden", "true");
  }

  /**
   * Handle search form submission
   */
  _onFormSubmit(event2) {
    if (this.inputElement.value === "") {
      event2.preventDefault();
      return;
    }

    // Build query with product type filter if applicable
    if (this.productTypeFilter !== "") {
      const cloneNode = this.inputElement.cloneNode();
      cloneNode.setAttribute("type", "hidden");
      cloneNode.value = `product_type:${this.productTypeFilter} AND ${this.inputElement.value}`;
      this.inputElement.removeAttribute("name");
      this.inputElement.insertAdjacentElement("afterend", cloneNode);
    }
  }

  /**
   * Handle product type changed
   */
  _productTypeChanged(event2, target) {
    this.productTypeFilter = target.value;
    this._doSearch();
  }

  /**
   * Load translations from data-settings
   */
  _loadTranslations() {
    try {
      const settings = this.getAttribute('data-settings');
      return settings ? JSON.parse(settings).translations : {};
    } catch (e) {
      console.warn('Failed to parse search bar translations:', e);
      return {};
    }
  }

  /**
   * Debounce utility
   */
  _debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Build SKU-aware search query
   * - If looks like SKU (alphanumeric with dashes/underscores, no spaces), query sku:"TERM" OR TERM
   * - Else, use TERM as-is
   */
  _buildSearchQuery(term) {
    const looksLikeSku = /^[A-Za-z0-9_-]+$/.test(term) && !/\s/.test(term);
    if (looksLikeSku) {
      return `sku:${term} OR ${term}`;
    }
    return term;
  }

  /**
   * Show default content for empty input (uses existing Liquid content)
   */
  _doSearchForEmptyInput() {
    // Conținutul pentru input gol este deja în HTML, doar afișăm rezultatele
    this.searchResultsElement.setAttribute("aria-hidden", "false");
  }

  /**
   * Show empty state when input is cleared
   */
  _showEmptyState() {
    this.classList.remove("is-filled");
    
    // Switch la empty state când search term-ul este gol
    const emptyStateElement = this.searchResultsElement.querySelector('.search-bar__empty-state');
    const resultsInnerElement = this.searchResultsElement.querySelector('.search-bar__results-inner');
    const skeletonContainer = this.searchResultsElement.querySelector('.skeleton-container');
    
    if (emptyStateElement && resultsInnerElement) {
      emptyStateElement.style.display = 'block';
      resultsInnerElement.style.display = 'none';
    }
    
    // Ascunde skeleton container
    if (skeletonContainer) {
      skeletonContainer.setAttribute('hidden', '');
    }
    
    // Afișează rezultatele
    this.searchResultsElement.setAttribute("aria-hidden", "false");
  }

  /**
   * Perform search
   */
  async _doSearch() {
    const searchTerm = this.inputElement.value.trim();
    
    if (searchTerm === "") {
      this._showEmptyState();
      return;
    }

    this.classList.add("is-filled");
    this.classList.add("is-loading");
    this.searchMenuElement?.setAttribute("aria-hidden", "true");
    
    // Ascunde empty state și results inner când se afișează loading
    const emptyStateElement = this.searchResultsElement.querySelector('.search-bar__empty-state');
    const resultsInnerElement = this.searchResultsElement.querySelector('.search-bar__results-inner');
    const skeletonContainer = this.searchResultsElement.querySelector('.skeleton-container');
    
    if (emptyStateElement && resultsInnerElement) {
      emptyStateElement.style.display = 'none';
      resultsInnerElement.style.display = 'none'; // Ascunde și results inner când loading
    }
    
    // Afișează skeleton container prin eliminarea atributului hidden
    if (skeletonContainer) {
      skeletonContainer.removeAttribute('hidden');
    }

    try {
      // Use the Search page with Section Rendering API
      // Build search URL with locale support
      const baseUrl = window.Shopify?.routes?.root || '';
      const searchUrl = `${baseUrl}search`;
      const url = new URL(searchUrl, window.location.origin);

      // Required parameters for section rendering with the `search` object
      url.searchParams.set('section_id', 'predictive-search');
      // Build query (SKU-aware)
      const finalQuery = this._buildSearchQuery(searchTerm);
      url.searchParams.set('q', finalQuery);
      // Improve partial matching similar to predictive
      url.searchParams.set('options[prefix]', 'last');
      // Types are filtered in Liquid; the search page doesn't accept comma-separated types reliably

      // Keep optional product type filter by augmenting the query
      if (this.productTypeFilter) {
        url.searchParams.set('q', `product_type:${this.productTypeFilter} AND (${finalQuery})`);
      }

      // Fetch both search section and predictive collections in parallel
      const predictiveUrl = new URL(`${baseUrl}search/suggest`, window.location.origin);
      predictiveUrl.searchParams.set('section_id', 'predictive-search');
      predictiveUrl.searchParams.set('q', searchTerm);
      // keep defaults; we only use collections from predictive branch

      const [searchResp, predictiveResp] = await Promise.all([
        fetch(url.toString()),
        fetch(predictiveUrl.toString())
      ]);
      
      if (!searchResp.ok) {
        const errorText = await searchResp.text();
        console.error('Search response not ok:', searchResp.status, searchResp.statusText);
        console.error('Error details:', errorText);
        throw new Error(`Search failed: ${searchResp.status} ${searchResp.statusText}`);
      }
      const [searchHtml, predictiveHtml] = await Promise.all([
        searchResp.text(),
        predictiveResp.ok ? predictiveResp.text() : Promise.resolve('')
      ]);

      // Merge: base on predictive, replace products with ones from search
      const mergedHtml = this._mergeSearchAndPredictiveCollections(searchHtml, predictiveHtml);
      this._displayResultsFromHTML(mergedHtml);
      this.searchResultsElement.setAttribute("aria-hidden", "false");
    } catch (error) {
      console.error('Search error:', error);
      this._displayError();
    } finally {
      this.classList.remove("is-loading");
      
      // Ascunde skeleton container prin adăugarea atributului hidden
      const skeletonContainer = this.searchResultsElement.querySelector('.skeleton-container');
      if (skeletonContainer) {
        skeletonContainer.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Display predictive search results from HTML response
   * The response comes from /search/suggest endpoint with section rendering
   */
  _displayResultsFromHTML(responseText) {
    const resultsContainer = this.searchResultsElement.querySelector('.search-bar__results-inner');
    if (!resultsContainer) {
      console.error('Search results container not found');
      return;
    }

    // Afișează results inner după ce s-au încărcat rezultatele
    resultsContainer.style.display = 'block';
    
    // Ascunde skeleton container când se afișează rezultatele
    const skeletonContainer = this.searchResultsElement.querySelector('.skeleton-container');
    if (skeletonContainer) {
      skeletonContainer.setAttribute('hidden', '');
    }

    // Create a temporary div to parse the HTML response
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = responseText;
    
    // Look for the predictive search results in the response
    // The response contains the rendered predictive-search section
    const searchContent = tempDiv.querySelector('.search-ajax');
    if (searchContent) {
      resultsContainer.innerHTML = searchContent.innerHTML;
    } else if (tempDiv.querySelector('.search-bar__result-products') || tempDiv.querySelector('.search-bar__result-linklist') || tempDiv.querySelector('.search-bar__result-promo')) {
      // Dacă nu există wrapperul, dar HTML-ul conține direct inner-ul, inserează-l ca atare
      resultsContainer.innerHTML = tempDiv.innerHTML;
    } else {
      // Fallback dacă nu avem niciun rezultat recognoscibil
      resultsContainer.innerHTML = `
        <div class="search-bar__no-results">
          <p>${this.translations.noResultsFor} "${this.inputElement.value}"</p>
        </div>
      `;
    }

    // Dacă avem conținut valid, asigură-te că empty-state-ul e ascuns
    const emptyStateElement = this.searchResultsElement.querySelector('.search-bar__empty-state');
    if (emptyStateElement) {
      emptyStateElement.style.display = 'none';
    }
  }

  /**
   * Merge predictive (base) + search: replace predictive products with search products
   * and ensure products appear before categories; keep predictive categories/articles/pages
   */
  _mergeSearchAndPredictiveCollections(searchHtml, predictiveHtml) {
    if (!predictiveHtml) return searchHtml;
    try {
      const tempSearch = document.createElement('div');
      tempSearch.innerHTML = searchHtml;
      const tempPredictive = document.createElement('div');
      tempPredictive.innerHTML = predictiveHtml;

      const searchAjax = tempSearch.querySelector('.search-ajax');
      const predictiveAjax = tempPredictive.querySelector('.search-ajax');
      if (!searchAjax) return searchHtml;

      // Find blocks
      const predictiveWrapper = predictiveAjax?.querySelector('div[data-source="predictive"]');
      const searchWrapper = searchAjax.querySelector('div[data-source="search"]');

      // Get data-conclusion values (total_count) from both HTMLs
      const searchConclusion = searchAjax?.querySelector('[data-conclusion]');
      const predictiveConclusion = predictiveAjax?.querySelector('[data-conclusion]');
      
      const searchTotalCount = searchConclusion ? parseInt(searchConclusion.getAttribute('data-conclusion') || '0', 10) : 0;
      const predictiveTotalCount = predictiveConclusion ? parseInt(predictiveConclusion.getAttribute('data-conclusion') || '0', 10) : 0;

      // If at least one has total_count > 1, use the one with count > 1
      if (searchTotalCount > 1 || predictiveTotalCount > 1) {
        // Use the one that has count > 1
        if (searchTotalCount > 1) {
          // Use search results - replace products in predictive with search products
          if (searchWrapper && predictiveWrapper) {
            predictiveWrapper.parentNode.replaceChild(searchWrapper.cloneNode(true), predictiveWrapper);
          } else if (searchWrapper && !predictiveWrapper && predictiveAjax) {
            // Search has products but predictive doesn't, add them
            const existingSearchWrapper = predictiveAjax.querySelector('div[data-source="search"]');
            if (!existingSearchWrapper) {
              const insertBefore = predictiveAjax.querySelector('.search-bar__result-linklist') ||
                                   predictiveAjax.querySelector('.search-bar__result-category') ||
                                   predictiveAjax.querySelector('.search-bar__view-all-wrapper') ||
                                   predictiveAjax.firstChild;
              if (insertBefore) {
                predictiveAjax.insertBefore(searchWrapper.cloneNode(true), insertBefore);
              } else {
                predictiveAjax.appendChild(searchWrapper.cloneNode(true));
              }
            }
          }
        }
        // If only predictive has count > 1, use predictive as-is (no replacement needed)
      } else {
        // Neither has count > 1, use search results if available
        if (searchWrapper && predictiveWrapper) {
          predictiveWrapper.parentNode.replaceChild(searchWrapper.cloneNode(true), predictiveWrapper);
        } else if (searchWrapper && !predictiveWrapper && predictiveAjax) {
          const existingSearchWrapper = predictiveAjax.querySelector('div[data-source="search"]');
          if (!existingSearchWrapper) {
            const insertBefore = predictiveAjax.querySelector('.search-bar__result-linklist') ||
                                 predictiveAjax.querySelector('.search-bar__result-category') ||
                                 predictiveAjax.querySelector('.search-bar__view-all-wrapper') ||
                                 predictiveAjax.firstChild;
            if (insertBefore) {
              predictiveAjax.insertBefore(searchWrapper.cloneNode(true), insertBefore);
            } else {
              predictiveAjax.appendChild(searchWrapper.cloneNode(true));
            }
          }
        }
      }

      return predictiveAjax.innerHTML;
    } catch (e) {
      console.warn('Merge predictive collections failed', e);
      return searchHtml;
    }
  }

  /**
   * Display error message
   */
  _displayError() {
    const resultsContainer = this.searchResultsElement.querySelector('.search-bar__results-inner');
    if (!resultsContainer) return;

    resultsContainer.innerHTML = `
      <div class="search-bar__error">
        <p>${this.translations.searchUnavailable}</p>
      </div>
    `;
    
    // Ascunde skeleton container când se afișează eroarea
    const skeletonContainer = this.searchResultsElement.querySelector('.skeleton-container');
    if (skeletonContainer) {
      skeletonContainer.setAttribute('hidden', '');
    }
    
    this.searchResultsElement.setAttribute("aria-hidden", "false");
  }

  /**
   * Handle mobile menu toggle button click
   * Interceptează click-ul când search-ul este deschis pentru a preveni deschiderea meniului
   */
  _onMobileMenuToggle(event) {
    // Verifică dacă search-ul este deschis
    if (this.classList.contains("is-open")) {
      // Închide search-ul și previne deschiderea meniului
      this._closeSearch();
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    // Dacă search-ul nu este deschis, lasă butonul să funcționeze normal
    // (va deschide/închide meniul mobil prin logica existentă)
  }

  /**
   * Close search and update mobile menu button label
   */
  _closeSearch() {
    this.classList.remove("is-open");
    this.isSearchOpen = false;
    
    // Închide rezultatele search-ului
    this.searchResultsElement.setAttribute("aria-hidden", "true");
    this.searchMenuElement?.setAttribute("aria-hidden", "true");
    
    // Elimină clasa de scroll blocat
    document.body.classList.remove("no-mobile-scroll");
    
    // Actualizează label-ul butonului
    this._updateMobileMenuButtonLabel();
  }

  /**
   * Update mobile menu button label and icon state based on current state
   */
  _updateMobileMenuButtonLabel() {
    if (!this.mobileMenuToggleElement) return;
    
    const isSearchOpen = this.classList.contains("is-open");
    const isMenuOpen = this.mobileMenuElement && this.mobileMenuElement.getAttribute("aria-hidden") === "false";
    
    if (isSearchOpen) {
      this.mobileMenuToggleElement.setAttribute("aria-label", this.translations.closeSearch);
      // Activează iconița X când search-ul este deschis
      this.mobileMenuToggleElement.setAttribute("aria-expanded", "true");
    } else if (isMenuOpen) {
      this.mobileMenuToggleElement.setAttribute("aria-label", this.translations.closeMenu);
      // Activează iconița X când meniul este deschis
      this.mobileMenuToggleElement.setAttribute("aria-expanded", "true");
    } else {
      this.mobileMenuToggleElement.setAttribute("aria-label", this.translations.openMenu);
      // Dezactivează iconița X când nici meniul, nici search-ul nu sunt deschise
      this.mobileMenuToggleElement.setAttribute("aria-expanded", "false");
    }
  }
}